import {
  ACCEPT_TOS,
  LAGER_CURRENT_USER,
  UPDATE_COMPANY_MAIN_CONTACT,
  USER_EMAIL_CHANGE,
  USER_INVITE,
  USER_PASSWORD_CHANGE,
} from '@/constants/api';
import { User } from '@/models/User';
import { getUserCompaniesQuery } from '@/schemas/getUserCompanies';
import {
  GetUserRecordsInCompaniesQueryType,
  GetUserRecordsInCompaniesVariablesType,
  getUserRecordsInCompaniesQuery,
} from '@/schemas/getUserRecordsInCompaniesQuery';

import {
  UpdateAuthUserMutationResponse,
  UpdateAuthUserMutationVariables,
  updateAuthUserMutation,
} from '@/schemas/updateAuthUserMutation';
import {
  UpdateUserProfileMutationResponse,
  UpdateUserProfileMutationVariables,
  updateUserProfileMutation,
} from '@/schemas/updateUserProfileMutation';
import { ChangeEmail, ChangePassword } from '@/types/auth';
import { MultipleCompaniesPartialTreydUser, SendInvite } from '@/types/user';
import { getApolloClient } from '@/utils/apollo';

import { proxy, save } from '@/utils/request';
import { REQUEST_METHOD } from '@treyd-io/core/constants/request';

export const getCurrentUser = async (accessToken: string) =>
  save<User>({
    url: LAGER_CURRENT_USER,
    method: REQUEST_METHOD.PUT,
    options: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        AUTHORIZATIONJWT: `Bearer ${accessToken}`,
      },
    },
  });
export const getUserCompanies = async (userId: number) => {
  return proxy<
    { lager_treyd_users: Array<MultipleCompaniesPartialTreydUser> },
    { userId: number }
  >({
    query: getUserCompaniesQuery,
    variables: { userId },
  });
};

export namespace GetUserPendingCompanies {
  export const server = async (userId: number, accessToken: string) => {
    const apolloClient = await getApolloClient(accessToken);
    return await apolloClient.query({
      query: getUserRecordsInCompaniesQuery,
      variables: { userId },
    });
  };

  export const client = (payload: GetUserRecordsInCompaniesVariablesType) => {
    return proxy<
      GetUserRecordsInCompaniesQueryType,
      GetUserRecordsInCompaniesVariablesType
    >({
      query: getUserRecordsInCompaniesQuery,
      variables: payload,
    });
  };
}

export const changePassword = ({
  oldPassword,
  newPassword,
}: ChangePassword.Payload) =>
  save<ChangePassword.Response>({
    url: USER_PASSWORD_CHANGE,
    method: REQUEST_METHOD.POST,
    data: { old_password: oldPassword, new_password: newPassword },
  });

export const changeEmail = ({ userId, email }: ChangeEmail.Payload) =>
  save<ChangeEmail.Response>({
    url: USER_EMAIL_CHANGE,
    method: REQUEST_METHOD.POST,
    data: { user_id: userId, email },
  });

export const sendInvite = (payload: SendInvite.Payload) => {
  return save<SendInvite.Response>({
    url: USER_INVITE,
    method: REQUEST_METHOD.POST,
    data: payload,
  });
};

export const updateUserProfile = async (
  payload: UpdateUserProfileMutationVariables
) => {
  return proxy<
    UpdateUserProfileMutationResponse,
    UpdateUserProfileMutationVariables
  >({
    mutation: updateUserProfileMutation,
    variables: payload,
  });
};

export const updateAuthUser = async (
  payload: UpdateAuthUserMutationVariables
) => {
  return proxy<UpdateAuthUserMutationResponse, UpdateAuthUserMutationVariables>(
    {
      mutation: updateAuthUserMutation,
      variables: payload,
    }
  );
};

export const acceptTermsOfService = (accessToken?: string) =>
  save({
    url: ACCEPT_TOS,
    method: REQUEST_METHOD.POST,
    options: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        AUTHORIZATIONJWT: `Bearer ${accessToken}`,
      },
    },
  });

export const updateMainContact = ({
  companyId,
  userId,
}: {
  companyId: number;
  userId: number;
}) => {
  return save({
    url: UPDATE_COMPANY_MAIN_CONTACT(companyId),
    method: REQUEST_METHOD.PUT,
    data: { contact_id: userId },
  });
};
